// ======================================================================
// FILE:
// src/modules/finance/transactions/receipt_voucher/pages/ReceiptVoucherListPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// PAGE:
// Receipt Voucher List
//
// PURPOSE:
// Displays Finance Receipt Voucher transactions.
//
// FEATURES:
// - KPI Summary
// - Search & Filters
// - Receipt Voucher Grid
// - Workflow Actions
// - Responsive layout
//
// IMPORTANT:
// - Uses existing Finance shared components
// - Uses existing financeLookupService
// - Uses receiptVoucherService
// - No Redux
// - No accounting logic
// ======================================================================

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import Select
    from "react-select";


// ======================================================================
// FINANCE COMMON COMPONENTS
// ======================================================================

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceSummaryCards
    from "../../../components/FinanceSummaryCards";

import FinanceSearchPanel
    from "../../../components/FinanceSearchPanel";

import FinanceStatusBadge
    from "../../../components/FinanceStatusBadge";

import FinanceActionButtons
    from "../../../components/FinanceActionButtons";


// ======================================================================
// SERVICES
// ======================================================================

import receiptVoucherService
    from "../services/receiptVoucherService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function ReceiptVoucherListPage() {

    const navigate =
        useNavigate();


    // ==================================================================
    // LOADING
    // ==================================================================

    const [
        loading,
        setLoading
    ] = useState(false);


    // ==================================================================
    // DATA
    // ==================================================================

    const [
        vouchers,
        setVouchers
    ] = useState([]);


    // ==================================================================
    // ERROR
    // ==================================================================

    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    // ==================================================================
    // COMPANY
    // ==================================================================

    const [
        selectedCompany,
        setSelectedCompany
    ] = useState(null);


    // ==================================================================
    // FILTERS
    // ==================================================================

    const [
        filters,
        setFilters
    ] = useState({

        company_id:
            null,

        search:
            "",

        status:
            "",

        payer_type:
            "",

        receipt_mode:
            "",

        date_from:
            "",

        date_to:
            "",

    });


    // ==================================================================
    // PAYER TYPE OPTIONS
    // ==================================================================

    const payerTypeOptions =
        useMemo(
            () => [

                {
                    value: "",
                    label: "All",
                },

                {
                    value: "BUSINESS_PARTNER",
                    label: "Business Partner",
                },

                {
                    value: "VENDOR",
                    label: "Vendor",
                },

                {
                    value: "CUSTOMER",
                    label: "Customer",
                },

                {
                    value: "EMPLOYEE",
                    label: "Employee",
                },

                {
                    value: "GOVERNMENT",
                    label: "Government / Tax Authority",
                },

                {
                    value: "OTHER",
                    label: "Other",
                },

            ],
            []
        );


    // ==================================================================
    // RECEIPT MODE OPTIONS
    // ==================================================================

    const receiptModeOptions =
        useMemo(
            () => [

                {
                    value: "",
                    label: "All",
                },

                {
                    value: "BANK_TRANSFER",
                    label: "Bank Transfer",
                },

                {
                    value: "NEFT",
                    label: "NEFT",
                },

                {
                    value: "RTGS",
                    label: "RTGS",
                },

                {
                    value: "IMPS",
                    label: "IMPS",
                },

                {
                    value: "CHEQUE",
                    label: "Cheque",
                },

                {
                    value: "CASH",
                    label: "Cash",
                },

                {
                    value: "OTHER",
                    label: "Other",
                },

            ],
            []
        );


    // ==================================================================
    // STATUS OPTIONS
    // ==================================================================

    const statusOptions =
        useMemo(
            () => [

                {
                    value: "",
                    label: "All",
                },

                {
                    value: "DRAFT",
                    label: "Draft",
                },

                {
                    value: "SUBMITTED",
                    label: "Submitted",
                },

                {
                    value: "APPROVED",
                    label: "Approved",
                },

                {
                    value: "POSTED",
                    label: "Posted",
                },

                {
                    value: "CANCELLED",
                    label: "Cancelled",
                },

            ],
            []
        );


    // ==================================================================
    // LOAD VOUCHERS
    // ==================================================================

    useEffect(
        () => {

            /*
             * Receipt Voucher list API requires company_id.
             *
             * Therefore we intentionally do not call the API until
             * a company has been selected.
             */

            if (
                !filters.company_id
            ) {

                setVouchers([]);

                return;

            }

            loadReceiptVouchers(
                filters
            );

        },
        [
            filters.company_id
        ]
    );


    // ==================================================================
    // LOAD RECEIPT VOUCHERS
    // ==================================================================

    async function loadReceiptVouchers(
        appliedFilters = filters
    ) {

        if (
            !appliedFilters.company_id
        ) {

            setVouchers([]);

            return;

        }


        try {

            setLoading(true);

            setErrorMessage("");


            const result =
                await receiptVoucherService
                    .listReceiptVouchers(
                        {

                            company_id:
                                appliedFilters.company_id,

                            search:
                                appliedFilters.search ||
                                undefined,

                            status:
                                appliedFilters.status ||
                                undefined,

                            payer_type:
                                appliedFilters.payer_type ||
                                undefined,

                            receipt_mode:
                                appliedFilters.receipt_mode ||
                                undefined,

                            date_from:
                                appliedFilters.date_from ||
                                undefined,

                            date_to:
                                appliedFilters.date_to ||
                                undefined,

                        }
                    );


            setVouchers(
                Array.isArray(
                    result.rows
                )
                    ? result.rows
                    : []
            );

        }

        catch (error) {

            console.error(
                "Failed to load Receipt Vouchers:",
                error
            );


            setVouchers([]);


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load Receipt Vouchers."
            );

        }

        finally {

            setLoading(false);

        }

    }


    // ==================================================================
    // UPDATE FILTER
    // ==================================================================

    function updateFilter(
        field,
        value
    ) {

        setFilters(
            previous => ({

                ...previous,

                [field]:
                    value,

            })
        );

    }


    // ==================================================================
    // SEARCH
    // ==================================================================

    function handleSearch() {

        if (
            !filters.company_id
        ) {

            setErrorMessage(
                "Please select a Company."
            );

            return;

        }


        loadReceiptVouchers(
            filters
        );

    }


    // ==================================================================
    // RESET
    // ==================================================================

    function handleReset() {

        const resetFilters = {

            company_id:
                selectedCompany?.value ||
                null,

            search:
                "",

            status:
                "",

            payer_type:
                "",

            receipt_mode:
                "",

            date_from:
                "",

            date_to:
                "",

        };


        setFilters(
            resetFilters
        );


        setErrorMessage("");


        if (
            resetFilters.company_id
        ) {

            loadReceiptVouchers(
                resetFilters
            );

        }

        else {

            setVouchers([]);

        }

    }


    // ==================================================================
    // NAVIGATION
    // ==================================================================

    function handleCreate() {

        navigate(
            "/finance/receipt-vouchers/create"
        );

    }


    function handleView(
        row
    ) {

        navigate(
            `/finance/receipt-vouchers/view/${row.id}`
        );

    }


    function handleEdit(
        row
    ) {

        navigate(
            `/finance/receipt-vouchers/edit/${row.id}`
        );

    }


    // ==================================================================
    // DELETE
    // ==================================================================

    async function handleDelete(
        row
    ) {

        if (
            !window.confirm(
                "Delete selected Receipt Voucher?"
            )
        ) {

            return;

        }


        try {

            setErrorMessage("");


            await receiptVoucherService
                .deleteReceiptVoucher(
                    row.id
                );


            loadReceiptVouchers(
                filters
            );

        }

        catch (error) {

            console.error(
                "Failed to delete Receipt Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to delete Receipt Voucher."
            );

        }

    }


    // ==================================================================
    // SUBMIT
    // ==================================================================

    async function handleSubmit(
        row
    ) {

        try {

            setErrorMessage("");


            await receiptVoucherService
                .submitReceiptVoucher(
                    row.id,
                    filters.company_id
                );


            loadReceiptVouchers(
                filters
            );

        }

        catch (
            error
        ) {

            console.error(
                "Failed to submit Receipt Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to submit Receipt Voucher."
            );

        }

    }


    // ==================================================================
    // APPROVE
    // ==================================================================

    async function handleApprove(
        row
    ) {

        try {

            setErrorMessage("");


            await receiptVoucherService
                .approveReceiptVoucher(
                    row.id,
                    filters.company_id
                );


            loadReceiptVouchers(
                filters
            );

        }

        catch (error) {

            console.error(
                "Failed to approve Receipt Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to approve Receipt Voucher."
            );

        }

    }


    // ==================================================================
    // POST
    // ==================================================================

    async function handlePost(
        row
    ) {

        try {

            setErrorMessage("");


            await receiptVoucherService
                .postReceiptVoucher(
                    row.id,
                    filters.company_id
                );


            loadReceiptVouchers(
                filters
            );

        }

        catch (error) {

            console.error(
                "Failed to post Receipt Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to post Receipt Voucher."
            );

        }

    }
        // ======================================================================
    // ASYNC COMPANY LOADER
    // ======================================================================

    async function loadCompanies(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );


            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name,

                })
            );

        }

        catch {

            return [];

        }

    }


    // ======================================================================
    // SUMMARY
    // ======================================================================

    const summary =
        useMemo(
            () => {

                const result = {

                    total:
                        vouchers.length,

                    draft:
                        0,

                    submitted:
                        0,

                    approved:
                        0,

                    posted:
                        0,

                    cancelled:
                        0,

                };


                vouchers.forEach(
                    voucher => {

                        const status =
                            String(
                                voucher.status ||
                                ""
                            ).toUpperCase();


                        if (
                            status === "DRAFT"
                        ) {

                            result.draft += 1;

                        }

                        else if (
                            status === "SUBMITTED"
                        ) {

                            result.submitted += 1;

                        }

                        else if (
                            status === "APPROVED"
                        ) {

                            result.approved += 1;

                        }

                        else if (
                            status === "POSTED"
                        ) {

                            result.posted += 1;

                        }

                        else if (
                            status === "CANCELLED"
                        ) {

                            result.cancelled += 1;

                        }

                    }
                );


                return result;

            },
            [
                vouchers
            ]
        );


    // ======================================================================
    // SUMMARY CARDS
    // ======================================================================

    const summaryCards = [

        {
            key:
                "total",

            title:
                "Total Vouchers",

            value:
                summary.total,

            icon:
                "fas fa-money-check-alt",

            background:
                "#0d6efd",
        },

        {
            key:
                "draft",

            title:
                "Draft",

            value:
                summary.draft,

            icon:
                "fas fa-pencil-alt",

            background:
                "#6c757d",
        },

        {
            key:
                "submitted",

            title:
                "Submitted",

            value:
                summary.submitted,

            icon:
                "fas fa-paper-plane",

            background:
                "#ffc107",
        },

        {
            key:
                "approved",

            title:
                "Approved",

            value:
                summary.approved,

            icon:
                "fas fa-check",

            background:
                "#198754",
        },

        {
            key:
                "posted",

            title:
                "Posted",

            value:
                summary.posted,

            icon:
                "fas fa-check-double",

            background:
                "#0dcaf0",
        },

    ];


    // ======================================================================
    // RENDER
    // ======================================================================

    return (

        <>

            {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-money-bill-wave"

                title="Receipt Voucher"

                subtitle={
                    "Create and manage Finance receipt vouchers."
                }

                breadcrumbs={[

                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Receipt Voucher",
                    },

                ]}

                actions={

                    <button

                        className="btn btn-primary"

                        onClick={
                            handleCreate
                        }

                    >

                        <i
                            className="
                                fas
                                fa-plus
                                me-2
                            "
                        />

                        New Receipt Voucher

                    </button>

                }

            />


            {/* ==========================================================
                SUMMARY
            ========================================================== */}

            <FinanceSummaryCards

                cards={
                    summaryCards
                }

            />


            {/* ==========================================================
                SEARCH / FILTERS
            ========================================================== */}

            <FinanceSearchPanel

                loading={
                    loading
                }

                onSearch={
                    handleSearch
                }

                onReset={
                    handleReset
                }

            >

                {/* ======================================================
                    COMPANY
                ====================================================== */}

                <div
                    className="
                        col-lg-3
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Company
                    </label>


                    <AsyncSelect

                        cacheOptions

                        defaultOptions

                        loadOptions={
                            loadCompanies
                        }

                        isClearable

                        value={
                            selectedCompany
                        }

                        onChange={
                            option => {

                                setSelectedCompany(
                                    option
                                );


                                updateFilter(
                                    "company_id",
                                    option?.value ||
                                    null
                                );

                            }
                        }

                    />

                </div>


                {/* ======================================================
                    SEARCH
                ====================================================== */}

                <div
                    className="
                        col-lg-3
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Search
                    </label>


                    <input

                        className="form-control"

                        value={
                            filters.search
                        }

                        onChange={
                            event =>
                                updateFilter(
                                    "search",
                                    event.target.value
                                )
                        }

                        placeholder="
                            Voucher No / Payer / Reference
                        "

                    />

                </div>


                {/* ======================================================
                    STATUS
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Status
                    </label>


                    <Select

                        options={
                            statusOptions
                        }

                        value={
                            statusOptions.find(
                                option =>
                                    option.value ===
                                    filters.status
                            )
                        }

                        onChange={
                            option =>
                                updateFilter(
                                    "status",
                                    option?.value ||
                                    ""
                                )
                        }

                    />

                </div>


                {/* ======================================================
                    PAYER TYPE
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Payer Type
                    </label>


                    <Select

                        options={
                            payerTypeOptions
                        }

                        value={
                            payerTypeOptions.find(
                                option =>
                                    option.value ===
                                    filters.payer_type
                            )
                        }

                        onChange={
                            option =>
                                updateFilter(
                                    "payer_type",
                                    option?.value ||
                                    ""
                                )
                        }

                    />

                </div>


                {/* ======================================================
                    RECEIPT MODE
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Receipt Mode
                    </label>


                    <Select

                        options={
                            receiptModeOptions
                        }

                        value={
                            receiptModeOptions.find(
                                option =>
                                    option.value ===
                                    filters.receipt_mode
                            )
                        }

                        onChange={
                            option =>
                                updateFilter(
                                    "receipt_mode",
                                    option?.value ||
                                    ""
                                )
                        }

                    />

                </div>


                {/* ======================================================
                    DATE FROM
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Date From
                    </label>


                    <input

                        type="date"

                        className="form-control"

                        value={
                            filters.date_from
                        }

                        onChange={
                            event =>
                                updateFilter(
                                    "date_from",
                                    event.target.value
                                )
                        }

                    />

                </div>


                {/* ======================================================
                    DATE TO
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Date To
                    </label>


                    <input

                        type="date"

                        className="form-control"

                        value={
                            filters.date_to
                        }

                        onChange={
                            event =>
                                updateFilter(
                                    "date_to",
                                    event.target.value
                                )
                        }

                    />

                </div>

            </FinanceSearchPanel>


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        {errorMessage}

                    </div>

                )
            }
                        {/* ==========================================================
                RECEIPT VOUCHER GRID
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                "
            >

                <div
                    className="card-body p-0"
                >

                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-hover
                                align-middle
                                mb-0
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        style={{
                                            width: 60,
                                        }}
                                    >
                                        #
                                    </th>

                                    <th>
                                        Voucher No
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Payer
                                    </th>

                                    <th>
                                        Payer Type
                                    </th>

                                    <th>
                                        Receipt Mode
                                    </th>

                                    <th
                                        className="text-end"
                                    >
                                        Amount
                                    </th>

                                    <th>
                                        Currency
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th>
                                        Approval
                                    </th>

                                    <th
                                        className="
                                            text-center
                                        "
                                        style={{
                                            width: 220,
                                        }}
                                    >
                                        Actions
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    loading ? (

                                        <tr>

                                            <td
                                                colSpan={11}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        spinner-border
                                                        text-primary
                                                    "
                                                    role="status"
                                                />

                                            </td>

                                        </tr>

                                    ) : !filters.company_id ? (

                                        <tr>

                                            <td
                                                colSpan={11}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        text-muted
                                                    "
                                                >

                                                    <i
                                                        className="
                                                            fas
                                                            fa-building
                                                            fa-3x
                                                            mb-3
                                                        "
                                                    />

                                                    <h5>
                                                        Select Company
                                                    </h5>

                                                    <p>
                                                        Select a Company
                                                        to view Receipt
                                                        Vouchers.
                                                    </p>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : vouchers.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan={11}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        text-muted
                                                    "
                                                >

                                                    <i
                                                        className="
                                                            fas
                                                            fa-money-bill-wave
                                                            fa-3x
                                                            mb-3
                                                        "
                                                    />

                                                    <h5>
                                                        No Receipt
                                                        Vouchers Found
                                                    </h5>

                                                    <p>
                                                        Create your first
                                                        Receipt Voucher.
                                                    </p>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : (

                                        vouchers.map(
                                            (
                                                row,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        row.id
                                                    }
                                                >

                                                    {/* ==================================
                                                        #
                                                    ================================== */}

                                                    <td>
                                                        {
                                                            index + 1
                                                        }
                                                    </td>


                                                    {/* ==================================
                                                        VOUCHER NO
                                                    ================================== */}

                                                    <td>

                                                        <button

                                                            className="
                                                                btn
                                                                btn-link
                                                                p-0
                                                            "

                                                            onClick={() =>
                                                                handleView(
                                                                    row
                                                                )
                                                            }

                                                        >

                                                            {
                                                                row.voucher_no ||
                                                                "-"
                                                            }

                                                        </button>

                                                    </td>


                                                    {/* ==================================
                                                        DATE
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.voucher_date
                                                                ? String(
                                                                    row.voucher_date
                                                                ).slice(
                                                                    0,
                                                                    10
                                                                )
                                                                : "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        PAYER
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.payer_name ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        PAYER TYPE
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.payer_type ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        RECEIPT MODE
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.receipt_mode ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        AMOUNT
                                                    ================================== */}

                                                    <td
                                                        className="
                                                            text-end
                                                        "
                                                    >

                                                        {
                                                            Number(
                                                                row.transaction_amount ||
                                                                0
                                                            ).toLocaleString(
                                                                "en-IN",
                                                                {
                                                                    minimumFractionDigits:
                                                                        2,

                                                                    maximumFractionDigits:
                                                                        2,
                                                                }
                                                            )
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        CURRENCY
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.currency_code ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        STATUS
                                                    ================================== */}

                                                    <td>

                                                        <FinanceStatusBadge

                                                            type="journal"

                                                            value={
                                                                row.status
                                                            }

                                                        />

                                                    </td>


                                                    {/* ==================================
                                                        APPROVAL
                                                    ================================== */}

                                                    <td>

                                                        <FinanceStatusBadge

                                                            type="approval"

                                                            value={
                                                                row.approval_status
                                                            }

                                                        />

                                                    </td>


                                                    {/* ==================================
                                                        ACTIONS
                                                    ================================== */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <FinanceActionButtons

                                                            row={
                                                                row
                                                            }

                                                            onView={
                                                                handleView
                                                            }

                                                            onEdit={
                                                                handleEdit
                                                            }

                                                            onDelete={
                                                                handleDelete
                                                            }

                                                            onSubmit={
                                                                handleSubmit
                                                            }

                                                            onApprove={
                                                                handleApprove
                                                            }

                                                            onPost={
                                                                handlePost
                                                            }

                                                        />

                                                    </td>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                RESULT COUNT
            ========================================================== */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mt-4
                "
            >

                <small
                    className="text-muted"
                >

                    Total Records:

                    <strong
                        className="ms-1"
                    >
                        {
                            vouchers.length
                        }
                    </strong>

                </small>

            </div>

        </>

    );

}
    